import {
  DropDown,
  Flex,
  FormControls,
  InfoTooltip,
  Switch,
  TextField,
} from '@elementary/common-ui/components';
import { Period } from '@elementary/common-ui/types';
import { Typography } from '@mui/material';
import {
  Controller,
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from 'react-hook-form';
import {
  DEFAULT_FAILURE_GRACE,
  detectionPeriodOptions,
} from 'shared/constants';
import { themeTokens } from '@elementary/common-ui/style/tokens';
import { space } from '@elementary/common-ui/style/utils';

type Props<TForm extends FieldValues> = {
  methods: UseFormReturn<TForm>;
  fieldPath: Path<TForm>;
  readOnly?: boolean;
};

const FailureGraceSection = <TForm extends FieldValues>({
  methods,
  fieldPath,
  readOnly,
}: Props<TForm>) => {
  const grace: Period | null | undefined = methods.watch(fieldPath);
  const isEnabled = grace?.period != null;

  return (
    <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
      <Flex sx={{ alignItems: 'center', gap: space('xs') }}>
        <FormControls.FormTitle>Failure grace</FormControls.FormTitle>
        <InfoTooltip
          size={16}
          title={
            <>
              Anomalies that exceed the expected range by less than this grace
              will not fail the test.
              <br />
              Useful when ingestion is slightly delayed by a temporary issue,
              giving the anomaly time to self-correct.
            </>
          }
        />
      </Flex>
      <FormControls.Row
        style={{
          gap: themeTokens.space.sm,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Switch
          checked={isEnabled}
          disabled={readOnly}
          onChange={(_, checked) =>
            methods.setValue(
              fieldPath,
              (checked ? DEFAULT_FAILURE_GRACE : null) as PathValue<
                TForm,
                Path<TForm>
              >,
              { shouldDirty: true },
            )
          }
        />
        <Typography component="span">
          Fail only if exceeded by more than
        </Typography>
        <Controller
          control={methods.control}
          name={fieldPath}
          render={({ field }) => (
            <Flex sx={{ gap: space('sm'), flexShrink: 0 }}>
              <TextField
                sx={{ width: '50px', minWidth: '50px' }}
                disabled={readOnly || !isEnabled}
                value={
                  Number.isFinite(field.value?.count) ? field.value.count : ''
                }
                onChange={(e) =>
                  field.onChange({
                    ...field.value,
                    count: parseInt(e.target.value, 10),
                  })
                }
              />
              <DropDown
                sortOptions={false}
                options={detectionPeriodOptions}
                sx={{ minWidth: '120px' }}
                value={field.value?.period}
                onChange={(period: Period['period']) =>
                  field.onChange({ ...field.value, period })
                }
                disabled={readOnly || !isEnabled}
              />
            </Flex>
          )}
        />
      </FormControls.Row>
    </FormControls.FieldsWrapper>
  );
};

export default FailureGraceSection;
